//
// ScrollFX -- state flipper for scrolly events
//

import {throttle} from 'throttle-debounce';


// Effect activation:
//   - If an active-class is specified, this class will also be toggled on the element.
//   - Events:
//     - Sends scrollfx/on & /off custom event on the element
//     - To fire an additional on or off event of your choosing, use data-scrollfx-on-event, -off-event
//
//
// Attributes:
//   data-scrollfx-times  -- number of times to run
//                           integer or string: 'infinity' or '∞' (unicode 0x221E), default '∞'
//   data-scrollfx-dir    -- direction of scroll to run effect
//                           string: 'both' / 'down', default both
//
//   data-scrollfx-active-class         -- class added when effect is switched on
//   data-scrollfx-active-remove-class  -- class removed when effect is switched on
//
//   data-scrollfx-on-event             -- custom events to fire (in addition to scrollfx/on & off)
//   data-scrollfx-off-event            -- ~
//
//   EITHER:
//   data-scrollfx-object-threshold  -- fraction of object that must be visible on the screen
//                                      before the effect is switched on.
//                                      default. "X%" or "0.Y". default value 1
//   OR:
//   data-scrollfx-screen-threshold  -- fraction of the way up the screen the object's top edge
//                                      must be before the effect is switched on.
//
//
// Explanation of threshold modes:
//
//
//              +------------------------------------------------+     ^
//     document |                     ^                          |     | scr_y
//              | +----+              | obj_t                    |     |
//              | |XXXX|              |                          |     |
//              | |XXXX|  +----+      v                          |     |
//              | +----+  |XXXX|  +----+                         |     |
//              |   c1    |XXXX|  |XXXX|                         |     |
//            +----------------------------------------------------+   v   ^
//   viewport | |         |    |  |    |                         | |       |
//            | |         |    |  |    |                         | |       | win_h
//            | |         +----+  |    |                         | |       |
//            | |           c2    |    |  +----+  ^              | |       |
//            | |                 |    |  |    |  | h            | |       |
//            | |                 |    |  |    |  |              | |       |
//            | |                 |    |  |    |  |              | |       |
//            | |                 |    |  +----+  v              | |       |
//            | |                 |    |    c4    +----+         | |       |
//            | |                 |    |          |    |         | |       |
//            +----------------------------------------------------+       v
//              |                 |XXXX|          |XXXX|         |
//              |                 +----+          |XXXX|  +----+ |
//              |                   c3            +----+  |XXXX| |
//              |                                   c5    |XXXX| |       a: above-the-fold part of object: top shaded area
//              |                                         +----+ |       b: below-the-fold part of object: btm shaded area
//              |                                           c6   |       ∂: threshold
//              +------------------------------------------------+
//
//
// The diagram shows the 6 possible situations, c1 to c6, for a box's vertical positioning
// on a screen with respect to the viewport.
//
// In 'object threshold' mode, the 'on' state is triggered when ∂% of the object is visible.
// This is true when:
//
//       h - (a + b)                           c1: { h               [ down: 0 ] }
//       -----------  > ∂              a =   c2,3: { scr_y - obj_t   [ down: 0 ] }
//            h                            c4,5,6: { 0                           }
//
//                                         c1,2,4: { 0                           }
//                                     b =   c3,5: { obj_t + h - (h_win + scr_y) }
//                                             c6: { h                           }
//
// When the effect direction is 'down', we also want the 'on' state applied in cases c1 & c2
// (& arguably also c3). This is achieved by simply setting a to 0 for c1-3.
//
//
// In 'screen threshold' mode, the 'on' state is triggered when the object is ∂% of the way up
// the screen.
// The threshold is reached when:
//
//     scr_y + win_h - obj_t
//     ---------------------  > ∂       Condition 1
//             win_h
//
// In this mode, if the direction option 'both', we somewhat arbitrarily also require the item
// not to be more than 70% scrolled off the screen:
//
//     obj_t > scr_y - h * 0.7          Condition 2
//


// Determine the expected state of the object
// ---------------------------------

function get_exp_state__object_mode(opts, window_metrics, object_metrics, previous_state) {
  var h = object_metrics.height;
  var obj_t = object_metrics.top_offset;
  var obj_b = obj_t + h;
  var scr_y = window_metrics.scroll_y;
  var win_h = window_metrics.height;
  var down_only = (opts.direction === 'down');

  var c = [
    (obj_b < scr_y),
    (obj_t < scr_y && obj_b > scr_y && obj_b < scr_y + win_h),
    (obj_t < scr_y && obj_b > scr_y && obj_b > scr_y + win_h),
    (obj_t > scr_y && obj_b < scr_y + win_h),
    (obj_t > scr_y && obj_t < scr_y + win_h && obj_b > scr_y + win_h),
    (obj_t > scr_y + win_h),
  ];
  c = c.indexOf(true) + 1;
  var a, b;
  switch (c) {
    case 1: { a = (down_only ? 0 : h);             b = 0;                     break; }
    case 2: { a = (down_only ? 0 : scr_y - obj_t); b = 0;                     break; }
    case 3: { a = (down_only ? 0 : scr_y - obj_t); b = obj_b - win_h - scr_y; break; }
    case 4: { a = 0;                               b = 0;                     break; }
    case 5: { a = 0;                               b = obj_b - win_h - scr_y; break; }
    case 6: { a = 0;                               b = h;                     break; }
  }

  return (1 - (a + b)/h) >= opts.threshold * 0.98;
}

function get_exp_state__screen_mode(opts, window_metrics, object_metrics) {
  var h = object_metrics.height;
  var obj_t = object_metrics.top_offset;
  var obj_b = obj_t + h;
  var scr_y = window_metrics.scroll_y;
  var win_h = window_metrics.height;

  var state = (scr_y + win_h - obj_t) / win_h > opts.threshold;
  if (opts.direction === 'both') {
    state &= (obj_t > scr_y - h * 0.7);
  }

  return state;
}


// Global window metrics cache
// ---------------------------------

var window_metrics = { };


// Globals
// ---------------------------------

var resize_throttle_period = 250;
var scroll_update_throttle_period = 50;
var n_controllers = 0;

var OFF = false;
var ON  = true;

var INFINITY = Math.pow(2, 53);

var event_on = 'scrollfx/on';
var event_off = 'scrollfx/off';

var default_options = {
  times: INFINITY,
  direction: 'both',
  threshold_mode: 'object',
  threshold: 1,
  active_class: null,
  active_remove_class: null
};


// Global callbacks (resize, load)
// ---------------------------------

function global_cb_resize() {
  window_metrics.width  = window.innerWidth;
  window_metrics.height = window.innerHeight;
}
var global_cb_resize__throttled = throttle(resize_throttle_period, global_cb_resize);
global_cb_resize();

function global_cb_scroll() {
  window_metrics.scroll_y = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop;
}
var global_cb_scroll__throttled = throttle(10, global_cb_scroll);

function global_cb_load(ev) {
  window.removeEventListener('load', global_cb_load);
  global_cb_resize();
  global_cb_scroll();
}
function global_bind() {
  window.addEventListener('resize', global_cb_resize__throttled);
  window.addEventListener('scroll', global_cb_scroll__throttled);
  window.addEventListener('load',   global_cb_load);
}
function global_unbind() {
  window.removeEventListener('resize', global_cb_resize__throttled);
  window.removeEventListener('scroll', global_cb_scroll__throttled);
  window.removeEventListener('load',   global_cb_load);
}


// get_options
// ---------------------------------

function get_options(el_wrapper) {
  var opts = { };
  function set_opt(attribute, opt_name) {
    const value = el_wrapper.getAttribute(`data-scrollfx-${attribute}`);
    if (value) {
      opts[opt_name] = value;
    }
  }
  set_opt('times',               'times');
  set_opt('dir',                 'direction');
  set_opt('active-class',        'active_class');
  set_opt('on-event',            'on_event');
  set_opt('off-event',           'off_event');
  set_opt('active-remove-class', 'active_remove_class');
  set_opt('object-threshold',    'threshold');
  set_opt('screen-threshold',    'threshold');

  if (opts.times !== undefined) {
    var n = opts.times;
    if (n === 'infinity' || n === '∞') {
      opts.times = INFINITY;
    }
    else if (n.match(/^\d+$/)) {
      opts.times = parseInt(n);
    }
    else {
      delete opts.times;
    }
  }

  if (opts.direction !== undefined) {
    if (!['down', 'both'].includes(opts.direction)) {
      delete opts.direction;
    }
  }

  if (opts.threshold !== undefined) {
    var t = opts.threshold;
    if (t.match(/^\d+(?:\.\d+)?$/)) {
      opts.threshold = parseFloat(t);
    }
    else if (t.match(/^\d+(?:\.\d+)?%$/)) {
      opts.threshold = parseFloat(t) / 100;
    }
    else {
      delete opts.threshold;
    }
  }

  opts.threshold_mode = el_wrapper.hasAttribute('data-scrollfx-screen-threshold') ? 'screen' : 'object';
  return Object.assign({}, default_options, opts);
}


// get_expected_state()
// ---------------------------------

function get_expected_state(opts, window_metrics, object_metrics, previous_state) {
  if (opts.threshold_mode === 'object') {
    return !!get_exp_state__object_mode(opts, window_metrics, object_metrics, previous_state);
  }
  else {
    return !!get_exp_state__screen_mode(opts, window_metrics, object_metrics, previous_state);
  }
}


// dispatch_state_update()
// ---------------------------------

function dispatch_state_update(el_wrapper, opts, state) {

  // Classes
  if (opts.active_class) {
    const active_classes = opts.active_class.split(/[ ,]+/).filter(Boolean);
    active_classes.forEach(c =>
      el_wrapper.classList[state ? 'add' : 'remove'](c)
    );
  }
  if (opts.active_remove_class) {
    const active_remove_classes = opts.active_remove_class.split(/[ ,]+/).filter(Boolean);
    active_remove_classes.forEach(c =>
      el_wrapper.classList[state ? 'remove' : 'add'](c)
    );
  }

  // Standard on/off event
  el_wrapper.dispatchEvent(new CustomEvent(state ? event_on : event_off));

  // Custom on/off events
  if (state && opts.on_event) {
    el_wrapper.dispatchEvent(new CustomEvent(opts.on_event));
  }
  if (!state && opts.off_event) {
    el_wrapper.dispatchEvent(new CustomEvent(opts.off_event));
  }
}


// The controller
// ---------------------------------

function init(el_wrapper) {
  var n_times_applied = 0;
  var object_metrics = null;
  var bound = false;
  var state = -1;
  var opts = get_options(el_wrapper);

  function update_object_metrics() {
    object_metrics = object_metrics || { };

    object_metrics.height = el_wrapper.offsetHeight;
    object_metrics.top_offset = el_wrapper.getBoundingClientRect().top + window_metrics.scroll_y;
    object_metrics.last_updated = Date.now();
  }

  // Check positions & send events
  //
  function update() {
    if (!object_metrics || Date.now() - object_metrics.last_updated > 1000) {
      update_object_metrics();
    }

    var expected_state = get_expected_state(opts, window_metrics, object_metrics, state);
    if (expected_state !== state) {
      // Dispatch state update
      state = expected_state;
      dispatch_state_update(el_wrapper, opts, state);

      // Unbind self if have reached the max # of times activated
      if (expected_state && ++n_times_applied >= opts.times) {
        unbind();
      }
    }
  }

  // Per-controller callbacks (resize, scroll, load)
  //
  function cb_scroll() {
    if (n_times_applied >= opts.times) {
      return;
    }

    update();
  }

  var cb_scroll_throttled = throttle(scroll_update_throttle_period, cb_scroll);

  function cb_resize(ev) {
    if (n_times_applied >= opts.times) {
      return;
    }
    update_object_metrics();
    update();
  }
  var cb_resize__throttled = throttle(resize_throttle_period, cb_resize);

  function cb_load() {
    window.removeEventListener('load', cb_load);
    cb_resize();
  }

  function bind() {
    if (!bound) {
      bound = true;

      window.addEventListener('resize', cb_resize__throttled);
      document.addEventListener('scroll', cb_scroll_throttled);
      window.addEventListener('load', cb_load);
    }
  }
  function unbind() {
    if (bound) {
      bound = false;

      window.removeEventListener('resize', cb_resize__throttled);
      document.removeEventListener('scroll', cb_scroll_throttled);
      window.removeEventListener('load', cb_load);

      if (--n_controllers === 0) {
        global_unbind();
      }
    }
  }

  if (++n_controllers === 1) {
    global_bind();
  }

  bind();

  return {
    unbind: unbind,
  };
}


// auto_init(): add items automatically
// ---------------------------------

function auto_init() {
  var items = document.querySelectorAll('[data-scrollfx]');
  Array.from(items).forEach(init);
}

export {
  init,
  auto_init,
};

